/**
 * useConnectionStatus - Dedicated hook for monitoring connection status
 * 
 * Best-in-class approach combining:
 * 1. Browser's Navigator.onLine API for instant network detection
 * 2. Periodic health check pings to verify server availability
 * 3. Separate from message polling for accurate, real-time status
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { log } from '../utils/logger';

/**
 * Configuration options for connection monitoring
 */
interface ConnectionStatusOptions {
  /** Base URL of the server to check */
  serverUrl: string;
  /** Health check interval in milliseconds (default: 15000 = 15s) */
  healthCheckInterval?: number;
  /** Timeout for health check requests in milliseconds (default: 5000 = 5s) */
  healthCheckTimeout?: number;
  /** Number of consecutive failures before marking as disconnected (default: 2) */
  failureThreshold?: number;
  /** Whether to start monitoring immediately (default: true) */
  enabled?: boolean;
}

/**
 * Connection status return value
 */
interface ConnectionStatusReturn {
  /** Whether the browser has network connectivity */
  isOnline: boolean;
  /** Whether the server is reachable */
  isServerReachable: boolean;
  /** Combined connection status (online AND server reachable) */
  isConnected: boolean;
  /** Timestamp of last successful health check */
  lastSuccessfulCheck: number | null;
  /** Manually trigger a health check */
  checkNow: () => Promise<boolean>;
}

/**
 * Custom hook for monitoring connection status
 * Combines browser network events with server health checks
 */
export const useConnectionStatus = ({
  serverUrl,
  healthCheckInterval = 15000,
  healthCheckTimeout = 5000,
  failureThreshold = 2,
  enabled = true
}: ConnectionStatusOptions): ConnectionStatusReturn => {
  // Browser network status
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  
  // Server reachability status
  const [isServerReachable, setIsServerReachable] = useState<boolean>(true);
  const [lastSuccessfulCheck, setLastSuccessfulCheck] = useState<number | null>(null);
  
  // Track consecutive failures
  const failureCountRef = useRef<number>(0);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Perform a health check to the server via the /healthz endpoint.
   * Treats "healthy" and "degraded" responses as reachable.
   */
  const performHealthCheck = useCallback(async (): Promise<boolean> => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      abortControllerRef.current?.abort();
    }, healthCheckTimeout);

    try {
      const cleanServerUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
      const checkUrl = `${cleanServerUrl}/healthz`;
      
      const response = await fetch(checkUrl, {
        method: 'GET',
        signal: abortControllerRef.current.signal,
        cache: 'no-store'
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Health check returned ${response.status}`);
      }

      const body = await response.json() as { status?: string };
      const reachable = body.status === 'healthy' || body.status === 'degraded';

      if (reachable) {
        failureCountRef.current = 0;
        setIsServerReachable(true);
        setLastSuccessfulCheck(Date.now());
      } else {
        throw new Error(`Unhealthy status: ${body.status}`);
      }

      return reachable;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Ignore abort errors (intentional cancellation)
      if (error instanceof Error && error.name === 'AbortError') {
        return isServerReachable;
      }

      failureCountRef.current += 1;
      
      // Only mark as unreachable after threshold is reached
      if (failureCountRef.current >= failureThreshold) {
        log(`Health check failed ${failureCountRef.current} times, marking server as unreachable`);
        setIsServerReachable(false);
      }
      
      return false;
    }
  }, [serverUrl, healthCheckTimeout, failureThreshold, isServerReachable]);

  /**
   * Public method to manually trigger a health check
   */
  const checkNow = useCallback(async (): Promise<boolean> => {
    return performHealthCheck();
  }, [performHealthCheck]);

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = (): void => {
      log('Browser went online');
      setIsOnline(true);
      // Immediately check server when coming back online
      performHealthCheck();
    };

    const handleOffline = (): void => {
      log('Browser went offline');
      setIsOnline(false);
      // Also mark server as unreachable when browser goes offline
      setIsServerReachable(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [performHealthCheck]);

  // Set up periodic health checks
  useEffect(() => {
    if (!enabled || !isOnline) {
      // Clear interval if disabled or offline
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      return;
    }

    // Initial health check
    performHealthCheck();

    // Set up interval for periodic checks
    healthCheckIntervalRef.current = setInterval(() => {
      performHealthCheck();
    }, healthCheckInterval);

    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, isOnline, healthCheckInterval, performHealthCheck]);

  // Combined connection status
  const isConnected = isOnline && isServerReachable;

  return {
    isOnline,
    isServerReachable,
    isConnected,
    lastSuccessfulCheck,
    checkNow
  };
};

